import React, { useState, useRef } from 'react';
import { User, X, Camera, Loader2, Check } from 'lucide-react';
import { ref as dbRef, set } from 'firebase/database';
import { database } from '../config/firebase';
import { uploadImageToStorage } from '../utils/imageToStorage';
import AddressAutocomplete from './AddressAutocomplete';
import { useModal } from '../hooks/useModal';
import { useToast } from './ToastProvider';

const BRAND = '#2563EB';

/**
 * ProfileModal — modifica profilo cliente (luglio 2026).
 *
 * Aggiornato con:
 * - Upload avatar reale su Firebase Storage (users/{uid}/avatar/profile_{ts})
 *   stesso pattern già usato per il banner sito in SitoSettings.js.
 * - Preview locale immediata (createObjectURL) prima dell'upload.
 * - avatarUrl salvato nel nodo users/{uid}/profile insieme agli altri campi.
 */
const ProfileModal = ({ user, profile, onClose, onSave, allowClose = true }) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    nome: profile?.nome || '',
    cognome: profile?.cognome || '',
    telefono: profile?.telefono || '',
    indirizzo: profile?.indirizzo || '',
    citta: profile?.citta || '',
    cap: profile?.cap || '',
    lat: typeof profile?.lat === 'number' ? profile.lat : null,
    lng: typeof profile?.lng === 'number' ? profile.lng : null,
    avatarUrl: profile?.avatarUrl || '',
  });

  const [avatarPreview, setAvatarPreview] = useState(profile?.avatarUrl || null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.warning('Formato non supportato. Usa JPG, PNG o WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.warning('File troppo grande. Massimo 5 MB.');
      return;
    }
    setAvatarFile(file);
    // Preview locale immediata, senza attendere l'upload
    const localUrl = URL.createObjectURL(file);
    setAvatarPreview(localUrl);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let finalAvatarUrl = formData.avatarUrl;

      // Upload avatar solo se è stato scelto un nuovo file
      if (avatarFile) {
        setUploadingAvatar(true);
        const path = `users/${user.uid}/avatar/profile_${Date.now()}`;
        finalAvatarUrl = await uploadImageToStorage(avatarFile, path, {
          maxSizeMB: 0.3,
          maxWidthOrHeight: 400,
        });
        setUploadingAvatar(false);
      }

      const toSave = { ...formData, avatarUrl: finalAvatarUrl, dataAggiornamento: new Date().toISOString() };
      const userProfileRef = dbRef(database, `users/${user.uid}/profile`);
      await set(userProfileRef, toSave);
      onSave(toSave);
    } catch (error) {
      console.error('Errore salvataggio profilo:', error);
      toast.error('Errore nel salvataggio del profilo. Riprova.');
    } finally {
      setSaving(false);
      setUploadingAvatar(false);
    }
  };

  const handlePlaceSelected = ({ indirizzo, citta, cap, lat, lng }) => {
    setFormData(prev => ({
      ...prev,
      indirizzo: indirizzo || prev.indirizzo,
      citta: citta || prev.citta,
      cap: cap || prev.cap,
      lat,
      lng,
    }));
  };

  const initial = formData.nome
    ? formData.nome.trim().charAt(0).toUpperCase()
    : (user.email || '?').charAt(0).toUpperCase();

  // Audit 2026-09-20, punto #11: accessibilità. Escape rispetta allowClose
  // (se il profilo è obbligatorio — es. primo accesso — Escape non deve
  // permettere di uscire, stessa regola già applicata al click sull'overlay).
  const modalRef = useModal(allowClose ? onClose : undefined);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 z-50"
      onClick={(e) => { if (allowClose && e.target === e.currentTarget) onClose(); }}
    >
      <div ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="profile-modal-title" className="bg-white rounded-2xl shadow-sm border border-slate-100 w-full max-w-2xl max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" style={{ color: BRAND }} />
              <div>
                <h2 id="profile-modal-title" className="text-lg font-bold text-slate-900">Il tuo profilo</h2>
                <p className="text-xs text-slate-400">Aggiorna i tuoi dati e la foto profilo</p>
              </div>
            </div>
            {allowClose && (
              <button onClick={onClose} className="text-slate-300 hover:text-slate-500 p-1">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Avatar upload */}
          <div className="flex flex-col items-center gap-3 pb-5 border-b border-slate-100">
            <div className="relative group">
              {/* Cerchio avatar */}
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden text-white font-black text-2xl shrink-0 ring-4 ring-white shadow-md"
                style={{ background: BRAND }}
              >
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  initial
                )}
              </div>
              {/* Overlay fotocamera al hover */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center transition-all"
                title="Cambia foto profilo"
              >
                <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-xs font-semibold hover:underline"
              style={{ color: BRAND }}
            >
              {avatarPreview ? 'Cambia foto' : 'Aggiungi foto profilo'}
            </button>
            {avatarPreview && avatarPreview !== profile?.avatarUrl && (
              <button
                type="button"
                onClick={() => { setAvatarPreview(profile?.avatarUrl || null); setAvatarFile(null); }}
                className="text-xs text-slate-400 hover:text-red-400"
              >
                Annulla cambio foto
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleAvatarChange}
            />
            <p className="text-[11px] text-slate-400">JPG, PNG o WebP · max 5 MB</p>
          </div>

          {/* Campi anagrafici */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Nome *</label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 transition"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Cognome *</label>
              <input
                type="text"
                value={formData.cognome}
                onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 transition"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Telefono *</label>
              <input
                type="tel"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 transition"
                required
                placeholder="+39 123 456 7890"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Indirizzo *</label>
              <AddressAutocomplete
                value={formData.indirizzo}
                onChange={(val) => setFormData(prev => ({ ...prev, indirizzo: val, lat: null, lng: null }))}
                onPlaceSelected={handlePlaceSelected}
                placeholder="Via Roma, 123"
                showHint={false}
                className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 transition"
              />
              {formData.lat && formData.lng && (
                <p className="text-[11px] mt-1.5 flex items-center gap-1 font-semibold text-emerald-600">
                  <Check className="w-3 h-3" /> Posizione confermata
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Città *</label>
              <input
                type="text"
                value={formData.citta}
                onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 transition"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">CAP *</label>
              <input
                type="text"
                value={formData.cap}
                onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 transition"
                required
                maxLength="5"
                placeholder="00100"
              />
            </div>
          </div>

          {/* Bottoni azione */}
          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm text-white transition disabled:opacity-60"
              style={{ background: BRAND }}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {uploadingAvatar ? 'Caricamento foto...' : 'Salvataggio...'}
                </>
              ) : (
                'Salva profilo'
              )}
            </button>
            {allowClose && (
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-slate-100 text-slate-600 py-3 rounded-xl hover:bg-slate-200 transition font-semibold text-sm"
              >
                Annulla
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileModal;
